import {NestedJobStop} from "../generated/graphql";
import {isCompleted} from "./stops/StopUtils";
import dayjs, {Dayjs} from "dayjs";

export const findEarliestJobStop = (stops: NestedJobStop[]): NestedJobStop | null => {
  const notCompletedStops = stops.filter((stop) => !isCompleted(stop));
  if (notCompletedStops.length === 0) {
    return null;
  }
  return notCompletedStops.reduce((earliest, current) => {
    const earliestJobStopTime = dayjs(earliest.scheduledDateTime);
    const currentJobStopTime = dayjs(current.scheduledDateTime);
    return currentJobStopTime.isBefore(earliestJobStopTime) ? current : earliest;
  }, notCompletedStops[0]);
};

export const findEarliestStartDate = (stops: NestedJobStop[]): Dayjs => {
  const currentDate = dayjs();
  const notCompletedStops = stops.filter((stop) => !isCompleted(stop));
  const earliestJobStop = findEarliestJobStop(notCompletedStops);
  if (!earliestJobStop) {
    return currentDate;
  }
  const rawDate = dayjs(earliestJobStop.scheduledDateTime);
  return rawDate.isAfter(currentDate) ? rawDate : currentDate;
};

export const combineDatesAndTime = (
  date: Dayjs | null | undefined,
  time: Dayjs | null | undefined,
  timeZone: string | null = null,
  keepLocalTime = false
): Dayjs => {
  if (!date || !time || !date.isValid() || !time.isValid()) {
    return dayjs("Invalid Date");
  }
  if (timeZone && timeZone !== Intl.DateTimeFormat().resolvedOptions().timeZone) {
    date = date.tz(timeZone, keepLocalTime);
    time = time.tz(timeZone, keepLocalTime);
  }

  return date.hour(time.hour()).minute(time.minute());
};

export const getTimeZoneOffset = (timeZone: string): number => {
  const date = dayjs().tz(timeZone);
  return date.utcOffset();
};

export const isStartLocationValid = (startLocation: string | null): boolean => {
  return !!startLocation && startLocation.trim().length > 0;
};

export const isFutureDateTime = (startDateTime: Dayjs | null | undefined): boolean => {
  if (!startDateTime?.isValid()) return false;
  const now = dayjs().startOf("minute");
  return !startDateTime.isBefore(now);
};

export const isFutureDate = (date: Dayjs | null | undefined): boolean => {
  if (!date?.isValid()) return false;
  return date.isAfter(dayjs().subtract(1, "day"), "day");
};
